'use client';
import { useEffect, useMemo } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { OT_CONSENT_COOKIE_NAME, isOtPerfEnabled } from '@ab-inbev-eu/utils/onetrust';
import type { Campaign } from '@segment/analytics-core';
import { datadogRum } from '@datadog/browser-rum';

import { publicConfig } from '../../utils/config';
import { getCookie } from '../../utils/functions.client';
import useDrag from '../../hooks/useDrag';
import { ERROR_MESSAGE } from '../../utils/constants';
import { useApplicationStore } from '../../stores/application.store';

import { AnalyticsContext } from './context';

const { SEGMENT, IS_DEV_MODE } = publicConfig();

type Props = React.PropsWithChildren & {
  uid?: string;
};

const enum UtmSearchParams {
  UTM_CAMPAIGN = 'utm_campaign',
  UTM_SOURCE = 'utm_source',
  UTM_MEDIUM = 'utm_medium',
}

const storageEntries = Object.entries({
  name: UtmSearchParams.UTM_CAMPAIGN,
  source: UtmSearchParams.UTM_SOURCE,
  medium: UtmSearchParams.UTM_MEDIUM,
}) as [keyof Campaign, UtmSearchParams][];

export function AnalyticsProvider({ children, uid }: Props) {
  const analytics = useMemo(() => new AnalyticsBrowser(), []);
  const { deltaY, isDragging } = useDrag();
  const selectStoreMode = useApplicationStore((s) => s.selectStoreMode);

  useEffect(() => {
    const campaignStore = window.sessionStorage.getItem('campaign');
    let campaign: Campaign | null = null;
    if (campaignStore) campaign = JSON.parse(campaignStore) as Campaign;
    const campaignUtmInfo: Partial<Campaign> = campaign ?? {};

    const searchParams = new URLSearchParams(window.location.search);

    // handle case with custom page to select store
    // https://ab-inbev.atlassian.net/browse/B2B2CE-20042
    if (!selectStoreMode && searchParams.has('selectStore')) {
      const querySelectStore = searchParams.get('selectStore');
      useApplicationStore.setState({ selectStoreMode: querySelectStore !== null });
    }

    storageEntries.forEach(([key, utmName]) => {
      const paramValue = searchParams.get(utmName);
      if (!paramValue) return;
      campaignUtmInfo[key] = paramValue;
    });

    // All three utm params should be present
    if (Object.keys(campaignUtmInfo).length !== storageEntries.length) return;

    window.sessionStorage.setItem('campaign', JSON.stringify(campaignUtmInfo));
  }, []);

  useEffect(() => {
    // Close keyboard when user scrolls up to fix
    //https://stackoverflow.com/questions/75834277/virtual-keyboard-in-safari-causes-black-space-under-app-body-and-scrolling-outsi
    const affectedScrolledHeight = -50;
    if (isDragging && deltaY < affectedScrolledHeight && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [isDragging, deltaY]);

  useEffect(() => {
    if (!SEGMENT.WRITE_KEY || IS_DEV_MODE) return;

    const initSegment = () => {
      try {
        if (!isOtPerfEnabled(decodeURIComponent(getCookie(OT_CONSENT_COOKIE_NAME) ?? ''))) return;
        analytics.load(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          { writeKey: SEGMENT.WRITE_KEY! },
          {
            user: {
              cookie: {
                key: uid,
              },
            },
          },
        );
        datadogRum.addAction('Segment initialized');
      } catch (e) {
        datadogRum.addError(e, {
          error_message: ERROR_MESSAGE.SEGMENT_INITIALIZATION,
        });
      }
    };

    initSegment();
    window.addEventListener('consent.onetrust', initSegment);
    return () => {
      window.removeEventListener('consent.onetrust', initSegment);
    };
  }, [analytics]);

  return <AnalyticsContext.Provider value={{ analytics }}>{children}</AnalyticsContext.Provider>;
}
